import { displayToKey, Meal } from "@/app/nutrition/meal.enum";
import { indexToOption, Option } from "@/app/nutrition/option.enum";
import React, {
  ChangeEventHandler,
  forwardRef,
  ForwardRefRenderFunction,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type FC,
} from "react";

import { t } from "i18next";
import { NutritionMealTableTabs } from "../molecules/NutritionMealTableTabs";
import { Empty } from "./Empty";

import { Select } from "./Select";
import { ButtonSoft } from "../atoms/ButtonSoft";
import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { MinusIcon, PlusIcon } from "lucide-react";
import { NutritionMealTableProduct } from "../molecules/NutritionMealTableProduct";
import { EditorMode } from "@/app/nutrition/editor-mode.enum";
import { NutritionMealTableSuplementation } from "../molecules/NutritionMealTableSuplementation";
import { NutritionMealTableAddProduct } from "../molecules/NutritionMealTableAddProduct";
import { portionToValue } from "@/app/nutrition/portion";
import { NutritionObjectives } from "@/app/nutrition-wizard/nutrition-objectives";
import { current } from "@reduxjs/toolkit";
import { isInMarginOfError } from "@/shared/utils/macros";
import { NutritionMealTableMacroIndicator } from "../molecules/NutritionMealTableMacroIndicator";
import { ug } from "date-fns/locale";

export type NutritionMealTableProps = {
  counter?: number;
  readonly?: boolean;
  hideTabs?: boolean;
  editWhenEmpty?: boolean;
  onEmptyClick?: () => void;
  onOptionDelete?: (meal: Meal, option: Option) => void;
  onOptionSwap?: (meal: Meal, option: Option) => void;
  onOptionRight?: (meal: Meal, option: Option) => void;
  onOptionLeft?: (meal: Meal, option: Option) => void;
  onCurrentOptionChange?: (option: Option, meal: Meal) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onSetProduct?: (
    product: any,
    meal: Meal,
    option: Option,
    index: number
  ) => void;
  onDeleteProduct?: (meal: Meal, option: Option, index: number) => void;
  suplementations: Array<any>;
  objective?: NutritionObjectives;
  editorMode: EditorMode;
  equivalences?: Record<string, Array<any>>;
  nutritionalInformations?: Record<string, any>;
  meal: Meal;
  options: Record<Option, any>;
  macros: Record<Option, any>;
  setFieldValue?: (field: string, value: any) => void;
  products?: Array<any>;
};

export type NutritionMealTableHandler = {
  setCurrentTab: (tab: number) => void;
  increaseKey: () => void;
};

export const NutritionMealTable: ForwardRefRenderFunction<
  NutritionMealTableHandler,
  NutritionMealTableProps
> = memo(
  forwardRef(
    (
      {
        counter,
        readonly,
        hideTabs,
        editorMode,
        equivalences,
        nutritionalInformations,
        suplementations,
        meal,
        options,
        macros,
        objective,
        onEmptyClick,
        onCurrentOptionChange,
        onChange,
        setFieldValue,
        onOptionDelete,
        onOptionSwap,
        onOptionRight,
        onOptionLeft,
        onSetProduct,
        onDeleteProduct,
        products,
        editWhenEmpty,
      },
      ref
    ) => {
      const [key, setKey] = useState(0);
      const [selectedProduct, setSelectedProduct] = useState<any>();
      const [showProducts, setShowProducts] = useState(false);
      const [showSuplementation, setShowSuplementation] = useState(false);
      const [currentTab, setCurrentTab] = useState(0);
      const [baseMacros, setBaseMacros] = useState<any>({
        fibers: 0,
        proteins: 0,
        fats: 0,
        calories: 0,
        carbohydrates: 0,
      });

      useImperativeHandle(ref, () => ({
        setCurrentTab: (tab: number) => {
          setCurrentTab(tab);
        },
        increaseKey: () => {
          setKey((key) => key + 1);
        },
      }));

      const handleProductDelete = (...args: Array<any>) => {
        setKey((key) => key + 1);
        onDeleteProduct(...args);
      };

      const { fibers, fats, carbohydrates, proteins, calories } =
        useMemo<any>(() => {
          const option = indexToOption(currentTab);
          if (!option)
            return {
              fibers: 0,
              proteins: 0,
              fats: 0,
              calories: 0,
              carbohydrates: 0,
            };

          const result = Object.values(macros[option][editorMode]).reduce(
            (_: any, macros: any) => {
              return {
                fibers: _?.fibers + macros?.fibers,
                fats: _?.fats + macros?.fats,
                calories: _?.calories + macros?.calories,
                carbohydrates: _?.carbohydrates + macros?.carbohydrates,
                proteins: _?.proteins + macros?.proteins,
              };
            },
            { fibers: 0, proteins: 0, fats: 0, calories: 0, carbohydrates: 0 }
          );

          if (currentTab == 0) {
            setBaseMacros(result);
          }

          return result;
        }, [macros, editorMode, currentTab, key]);

      useEffect(() => {
        if (currentTab != undefined) {
          setShowSuplementation(false);
        }

        onCurrentOptionChange(indexToOption(currentTab), meal);
      }, [currentTab]);

      const option = options[indexToOption(currentTab)];

      const handlePortionAtlChange = useCallback(
        (
          portion: string,
          meal: Meal,
          option: Option,
          index: number,
          editorMode: EditorMode
        ) => {
          if (readonly) return () => {};
          if (editorMode == EditorMode.TrainingDay) {
            setFieldValue(
              `nutrition.[${meal}].[${option}].products[${index}].portionAlt`,
              portion
            );
          } else {
            setFieldValue(
              `nutrition.[${meal}].[${option}].products[${index}].portionRestAlt`,
              portion
            );
          }
        },
        [readonly]
      );

      const handleProductChange = useCallback((product) => {
        setSelectedProduct(() => product);
      }, []);

      const handleAccept = useCallback(() => {
        onSetProduct(
          selectedProduct,
          meal,
          indexToOption(currentTab),
          option.products?.length
        );

        setSelectedProduct(undefined);
        setShowProducts(false);
      }, [selectedProduct]);

      const handleCancel = useCallback(() => {
        setSelectedProduct(undefined);
        setShowProducts(false);
      }, []);

      useEffect(() => {
        Object.keys(options).forEach((optionKey) => {
          console.debug("optionKey");
          console.debug(optionKey);

          const option = options[optionKey];

          if (!option) return;

          console.debug("option value");
          console.debug(option);

          option["products"]?.forEach((product, index) => {
            setFieldValue(
              `nutrition.${meal}.[${optionKey}].products[${index}].portion`,
              product.portion
            );

            const portionValue = portionToValue(product.portion);

            queueMicrotask(() => {
              const nutritionalInformation =
                nutritionalInformations[product.product];

              const macros = {
                carbohydrates:
                  nutritionalInformation.carbohydrates * portionValue,
                fats: nutritionalInformation.fats * portionValue,
                fibers: nutritionalInformation.fibers * portionValue,
                proteins: nutritionalInformation.proteins * portionValue,
                calories: nutritionalInformation.calories * portionValue,
              };

              setFieldValue(
                `macros.${meal}.[${optionKey}].trainingDay.[${index}]`,
                macros
              );
            });
          });
        });

        /* 
        console.debug(options);
        for (const option in options) {
          console.debug("OPTION");
          console.debug(option);
          for (const type in options[option]) {
            console.debug("TYPE");
            console.debug(type);

            console.debug(options[option][type]);
            if (!options[option][type]?.products) {
              console.debug("NO PRODUCTS");
              continue;
            }

            for (const product of options[option][type]?.products) {
              const portionValue = portionToValue(event.target.value);

              setFieldValue(`nutrition.${meal}.[${option}].products[${options[option][type]?.products.indexOf(product)}].portion`, event.target.value);

              queueMicrotask(() => {
                const nutritionalInformation = nutritionalInformations[product];

                const macros = {
                  carbohydrates:
                    nutritionalInformation.carbohydrates * portionValue,
                  fats: nutritionalInformation.fats * portionValue,
                  fibers: nutritionalInformation.fibers * portionValue,
                  proteins: nutritionalInformation.proteins * portionValue,
                  calories: nutritionalInformation.calories * portionValue,
                };

                setFieldValue(
                  `macros.${meal}.[${option}].[${type}].[${index}]`,
                  macros
                );
              });
            }
          }
        } */
      }, [key]);

      const handlePortionChange: ChangeEventHandler<HTMLInputElement> =
        useCallback(
          (event) => {
            if (readonly) return () => {};
            const type = event.target.getAttribute("data-type");
            const index = event.target.getAttribute("data-index");
            const meal = event.target.getAttribute("data-meal");
            const option = event.target.getAttribute("data-option");
            const product = event.target.getAttribute("data-product");

            const portionValue = portionToValue(event.target.value);

            setFieldValue(event.target.name, event.target.value);

            queueMicrotask(() => {
              const nutritionalInformation = nutritionalInformations[product];

              const macros = {
                carbohydrates:
                  nutritionalInformation.carbohydrates * portionValue,
                fats: nutritionalInformation.fats * portionValue,
                fibers: nutritionalInformation.fibers * portionValue,
                proteins: nutritionalInformation.proteins * portionValue,
                calories: nutritionalInformation.calories * portionValue,
              };

              console.debug(`macros.${meal}.[${option}].[${type}].[${index}]`);
              setFieldValue(
                `macros.${meal}.[${option}].[${type}].[${index}]`,
                macros
              );
            });
          },
          [nutritionalInformations, readonly]
        );

      const calculateValueToDisplay = useCallback(
        (type: string, value: number) => {
          const range = type == "calories" ? 25 : 5;
          const unit = type == "calories" ? "kcal" : "gr";
          const difference = Math.abs(baseMacros[type] - value);
          if (difference <= range) return "✅";
          else {
            return (difference * -1).toFixed(0) + ` ${unit}`;
          }
        },
        [baseMacros]
      );

      const handleShowSupplementation = useCallback(() => {
        setShowSuplementation(true);
        setCurrentTab(undefined);
      }, []);

      return (
        <div className="mt-10">
          {!hideTabs && (
            <Fragment>
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                {t(`nutrition-wizard.views.${displayToKey(meal)}`)}
              </h1>
              <NutritionMealTableTabs
                counter={counter}
                objective={objective}
                readonly={readonly}
                meal={meal}
                option={option}
                onOptionDelete={onOptionDelete}
                onOptionSwap={onOptionSwap}
                onOptionLeft={onOptionLeft}
                onOptionRight={onOptionRight}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                showSupplementation={showSuplementation}
                onShowSuplementation={handleShowSupplementation}
              />
            </Fragment>
          )}
          <div>
            {objective !== NutritionObjectives.Health &&
              !showSuplementation && (
                <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 px-5 sm:px-10 border-b border-gray-200 pb-5">
                  <NutritionMealTableMacroIndicator
                    label={t("nutrition.views.calories")}
                    main={currentTab == 0}
                    value={calories?.toFixed(2)}
                    indicator={calculateValueToDisplay("calories", calories)}
                    unit="kcal"
                    empty={option?.products?.length === 0}
                  />
                  <NutritionMealTableMacroIndicator
                    label={t("nutrition.views.proteins")}
                    main={currentTab == 0}
                    value={proteins?.toFixed(2)}
                    indicator={calculateValueToDisplay("proteins", proteins)}
                    unit="gr"
                    empty={option?.products?.length === 0}
                  />
                  <NutritionMealTableMacroIndicator
                    label={t("nutrition.views.carbohydrates")}
                    main={currentTab == 0}
                    value={carbohydrates?.toFixed(2)}
                    indicator={calculateValueToDisplay(
                      "carbohydrates",
                      carbohydrates
                    )}
                    unit="gr"
                    empty={option?.products?.length === 0}
                  />
                  <NutritionMealTableMacroIndicator
                    label={t("nutrition.views.fibers")}
                    main={currentTab == 0}
                    value={fibers?.toFixed(2)}
                    indicator={calculateValueToDisplay("fibers", fibers)}
                    unit="gr"
                    empty={option?.products?.length === 0}
                  />
                  <NutritionMealTableMacroIndicator
                    label={t("nutrition.views.fats")}
                    main={currentTab == 0}
                    value={fats?.toFixed(2)}
                    indicator={calculateValueToDisplay("fats", fats)}
                    unit="gr"
                    empty={option?.products?.length === 0}
                  />
                </dl>
              )}
            <div className="px-2">
              <div className="flow-root">
                <div className="-mx-4 overflow-visible sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full px-3 py-2 align-middle sm:px-6 lg:px-8">
                    {(editWhenEmpty
                      ? false
                      : showSuplementation
                        ? !suplementations.length
                        : !option?.products?.length) && (
                      <Empty
                        texto={
                          showSuplementation
                            ? t("contexts.nutrition.no_supplements")
                            : t("contexts.nutrition.no_products")
                        }
                        onClick={onEmptyClick}
                      />
                    )}
                    {showSuplementation && (
                      <Fragment>
                        <Fragment>
                          <table className="min-w-full divide-y divide-gray-300">
                            <colgroup>
                              <col style={{ width: "40%" }} />
                              <col style={{ width: "30%" }} />
                              <col style={{ width: "30%" }} />
                            </colgroup>
                            <thead>
                              <tr>
                                <th
                                  scope=""
                                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                                ></th>
                                <th
                                  scope=""
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  {t("nutrition-wizard.views.products")}
                                </th>
                                <th
                                  scope=""
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  {t("nutrition-wizard.views.gr-ml")}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              <tr key={1}>
                                <td
                                  rowSpan={
                                    true === true
                                      ? suplementations?.length + 3
                                      : suplementations?.length + 2
                                  }
                                  className="whitespace-normal px-4 py-5 text-sm text-gray-500 align-top w-1/4" // Reduce el padding a 0
                                >
                                  <div className="flex justify-center items-center">
                                    <img
                                      src={`https://dash.fitmewise.com/images/nutrition/${suplementations.at(0)?.image}`}
                                      alt=""
                                      className="rounded-lg mx-0"
                                      style={{
                                        maxWidth: "300px",
                                        maxHeight: "300px",
                                      }}
                                    />
                                  </div>
                                </td>
                              </tr>
                              {suplementations?.map((suplementation, index) => {
                                return (
                                  <NutritionMealTableSuplementation
                                    meal={meal}
                                    onChange={onChange}
                                    index={index}
                                    editorMode={editorMode}
                                    key={`nutrition-meal-table-suplementation-${meal}-${index}`}
                                    suplementation={suplementation}
                                  />
                                );
                              })}
                            </tbody>
                          </table>
                        </Fragment>
                      </Fragment>
                    )}
                    {(showSuplementation
                      ? suplementations.length > 0
                      : (editWhenEmpty ? true : option?.products?.length > 0)) && (
                      <Fragment>
                        <table className=" divide-y divide-gray-300 table-fixed min-w-[1000px]">
                          <colgroup>
                            {objective === NutritionObjectives.Health ? (
                              <Fragment>
                                <col style={{ width: "30%" }} />
                                <col style={{ width: "40%" }} />
                                <col style={{ width: "30%" }} />
                              </Fragment>
                            ) : (
                              <Fragment>
                                <col style={{ width: "30%" }} />
                                <col style={{ width: "40%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "20%" }} />
                              </Fragment>
                            )}
                          </colgroup>
                          <thead>
                            <tr>
                              <th
                                scope=""
                                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                              ></th>
                              <th
                                scope=""
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                              >
                                {t("nutrition-wizard.views.products")}
                              </th>
                              {objective !== NutritionObjectives.Health && (
                                <th
                                  scope=""
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  {t("nutrition-wizard.views.gr-ml")}
                                </th>
                              )}
                              <th
                                scope=""
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                              >
                                {t("nutrition-wizard.views.portions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            <tr key={1}>
                              <td
                                rowSpan={100}
                                className="whitespace-normal px-4 py-5 text-sm text-gray-500 align-top w-1/4" // Reduce el padding a 0
                              >
                                <div className="flex justify-center items-center">
                                  <img
                                    src={
                                      option?.gallery?.url ??
                                      `https://dash.fitmewise.com/images/nutrition/${option?.gallery?.image}`
                                    }
                                    alt=""
                                    className="rounded-lg mx-0" // Asegúrate de que no haya márgenes
                                    style={{
                                      maxWidth: "300px", // Ajusta a 100% para ocupar todo el espacio disponible
                                      maxHeight: "300px", // Mantener altura máxima
                                    }}
                                  />
                                </div>
                              </td>
                            </tr>
                            {option?.products?.map((product, index) => {
                              return (
                                <NutritionMealTableProduct
                                  key={`nutrition-meal-table-product-${meal}-${option}-${index}`}
                                  editorMode={editorMode}
                                  onPortionAltChange={handlePortionAtlChange}
                                  equivalences={equivalences[product.product]}
                                  product={product}
                                  meal={meal}
                                  option={indexToOption(currentTab)}
                                  index={index}
                                  onChange={handlePortionChange}
                                  onDelete={handleProductDelete}
                                />
                              );
                            })}
                            {!readonly && (
                              <NutritionMealTableAddProduct
                                hidden={!showProducts}
                                product={selectedProduct}
                                onProductChange={handleProductChange}
                                products={products ?? []}
                                onAccept={handleAccept}
                                onCancel={handleCancel}
                              />
                            )}
                            {!readonly && (
                              <tr key={3}>
                                <td
                                  colSpan={5}
                                  className={
                                    "whitespace-nowrap px-3 py-5 text-sm text-gray-500 "
                                  }
                                >
                                  <div className="flex justify-center items-center">
                                    <button
                                      onClick={() =>
                                        setShowProducts(
                                          (showProducts) => !showProducts
                                        )
                                      }
                                      type="button"
                                      className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
                                      aria-label="Add"
                                    >
                                      {showProducts ? (
                                        <MinusIcon
                                          aria-hidden="true"
                                          className="h-5 w-5"
                                        />
                                      ) : (
                                        <PlusIcon
                                          aria-hidden="true"
                                          className="h-5 w-5"
                                        />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Fragment>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  )
);
